<template>
  <div class="deal-settings">
    <!-- Deal details -->
    <section class="deal-settings__card">
      <h4 class="deal-settings__card-title">{{ $t('deals.settings.deal_details') }}</h4>

      <div class="deal-settings__field">
        <label for="deal-title">{{ $t('deals.settings.deal_name') }}</label>
        <input
          id="deal-title"
          class="deal-settings__control"
          type="text"
          v-model="editForm.title"
          :placeholder="$t('deals.title_placeholder')"
        />
      </div>

      <div class="deal-settings__row">
        <div class="deal-settings__field">
          <label for="deal-value">{{ $t('deals.value') }}</label>
          <div class="deal-settings__control deal-settings__control--prefixed">
            <span class="deal-settings__prefix">€</span>
            <input
              id="deal-value"
              type="number"
              v-model="editForm.value"
              :placeholder="$t('deals.value_placeholder')"
            />
          </div>
        </div>

        <div class="deal-settings__field">
          <label for="deal-close-date">{{ $t('deals.expected_close_date') }}</label>
          <input
            id="deal-close-date"
            class="deal-settings__control"
            type="date"
            v-model="editForm.expected_close_date"
          />
        </div>
      </div>

      <div class="deal-settings__row">
        <div class="deal-settings__field">
          <label for="deal-source">{{ $t('deals.source') }}</label>
          <div
            class="deal-settings__control"
            :class="{ 'deal-settings__control--locked': sourceLocked }"
          >
            <input
              id="deal-source"
              type="text"
              v-model="editForm.source"
              :disabled="sourceLocked"
              :placeholder="$t('deals.source_placeholder')"
            />
            <BaseIcon v-if="sourceLocked" name="lock" size="16" />
          </div>
          <span v-if="sourceLocked" class="deal-settings__field-hint">
            {{ $t('deals.settings.source_locked') }}
          </span>
        </div>

        <div class="deal-settings__field">
          <label for="deal-owner">{{ $t('deals.owner') }}</label>
          <div class="deal-settings__control deal-settings__control--select">
            <select id="deal-owner" v-model="editForm.owner_id">
              <option :value="null">{{ $t('deals.select_owner') }}</option>
              <option v-for="option in ownerOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
            <BaseIcon name="chevron" size="16" :rotate="180" />
          </div>
        </div>
      </div>

      <div class="deal-settings__field">
        <label for="deal-notes">{{ $t('deals.internal_notes') }}</label>
        <textarea
          id="deal-notes"
          class="deal-settings__control deal-settings__control--textarea"
          v-model="editForm.internal_notes"
          :placeholder="$t('deals.internal_notes_placeholder')"
          rows="3"
        ></textarea>
      </div>

      <div class="deal-settings__divider"></div>

      <div class="deal-settings__actions">
        <button
          type="button"
          class="deal-settings__btn deal-settings__btn--plain"
          :disabled="!hasChanges || saving"
          @click="resetForm"
        >
          {{ $t('common.cancel') }}
        </button>
        <Btn
          variant="secondary"
          size="sm"
          :disabled="!hasChanges"
          :loading="saving"
          @click="saveChanges"
        >
          {{ $t('common.save_changes') }}
        </Btn>
      </div>
    </section>

    <!-- Manage -->
    <section class="deal-settings__card deal-settings__card--rows">
      <div class="deal-settings__manage-row">
        <div class="deal-settings__manage-text">
          <strong>{{ $t('deals.merge_deals') }}</strong>
          <span>{{ $t('deals.settings.merge_description') }}</span>
        </div>
        <button type="button" class="deal-settings__btn" @click="handleMerge">
          <BaseIcon name="merge" size="16" />
          <span>{{ $t('deals.merge_deals') }}</span>
        </button>
      </div>

      <div class="deal-settings__divider"></div>

      <div class="deal-settings__manage-row">
        <div class="deal-settings__manage-text">
          <strong>{{ $t('deals.delete_deal') }}</strong>
          <span>{{ $t('deals.settings.delete_description') }}</span>
        </div>
        <button
          type="button"
          class="deal-settings__btn deal-settings__btn--danger"
          @click="handleDelete"
        >
          <BaseIcon name="trash" size="16" />
          <span>{{ $t('common.delete') }}</span>
        </button>
      </div>
    </section>
  </div>
</template>

<script>
  import { ref, computed, watch, onMounted, getCurrentInstance } from 'vue';
  import { useDealStore } from '@/store/dealStore';
  import { useUserStore } from '@/store/userStore';
  import { useToastStore } from '@/store/toastStore';
  import { useUIStore } from '@/store/uiStore';
  import OrganizationService from '@/services/organizationService';

  import Btn from '@/components/partials/Btn.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';

  const organizationService = new OrganizationService();

  export default {
    name: 'DealSettings',

    components: {
      Btn,
      BaseIcon,
    },

    props: {
      dealId: {
        type: String,
        required: true,
      },
      deal: {
        type: Object,
        required: true,
      },
    },

    emits: ['deal-updated', 'delete', 'deal-merged'],

    setup(props, { emit }) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const dealStore = useDealStore();
      const userStore = useUserStore();
      const toastStore = useToastStore();
      const uiStore = useUIStore();

      const saving = ref(false);
      const members = ref([]);
      const editForm = ref({
        title: '',
        value: 0,
        expected_close_date: '',
        source: '',
        owner_id: null,
        internal_notes: '',
      });

      const ownerOptions = computed(() => {
        if (!Array.isArray(members.value)) return [];
        return members.value.map((member) => {
          const user = member.user || member;
          const id = user?.id ?? member.user_id;
          const label =
            [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() ||
            user?.email ||
            member.email ||
            id;
          return { value: id, label };
        });
      });

      // Deals that came in through a form keep their funnel attribution
      const sourceLocked = computed(() =>
        Boolean(props.deal?.source_submission_id || props.deal?.source_submission)
      );

      const hasChanges = computed(() => {
        if (!props.deal) return false;
        const dealOwnerId = props.deal.owner_id ?? null;
        const formOwnerId = editForm.value.owner_id ?? null;
        return (
          editForm.value.title !== (props.deal.title || '') ||
          Number(editForm.value.value) !== (props.deal.value || 0) ||
          editForm.value.expected_close_date !== (props.deal.expected_close_date || '') ||
          editForm.value.source !== (props.deal.source || '') ||
          formOwnerId !== dealOwnerId ||
          editForm.value.internal_notes !== (props.deal.internal_notes || '')
        );
      });

      const fetchMembers = async () => {
        try {
          const organizationId = userStore.currentOrganization?.id;
          if (!organizationId) {
            members.value = [];
            return;
          }
          members.value = await organizationService.getMembers(organizationId);
        } catch (error) {
          console.error('Failed to fetch members:', error);
          members.value = [];
        }
      };

      const resetForm = () => {
        const deal = props.deal;
        if (!deal) return;
        editForm.value = {
          title: deal.title || '',
          value: deal.value || 0,
          expected_close_date: deal.expected_close_date || '',
          source: deal.source || '',
          owner_id: deal.owner_id ?? null,
          internal_notes: deal.internal_notes || '',
        };
      };

      // Initialize form when deal changes
      watch(() => props.deal, resetForm, { immediate: true });

      const saveChanges = async () => {
        saving.value = true;

        try {
          await dealStore.updateDeal(props.dealId, {
            title: editForm.value.title,
            value: Number(editForm.value.value),
            expected_close_date: editForm.value.expected_close_date || null,
            source: editForm.value.source || null,
            owner_id: editForm.value.owner_id || null,
            internal_notes: editForm.value.internal_notes || null,
          });

          toastStore.addToast({
            type: 'success',
            message: $t('deals.deal_updated'),
          });

          emit('deal-updated', props.deal);
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: $t('deals.update_failed'),
          });
        } finally {
          saving.value = false;
        }
      };

      const handleMerge = () => {
        import('@/components/deals/MergeDealsModal.vue').then((module) => {
          const MergeDealsModal = module.default;
          const modalId = uiStore.addModal(MergeDealsModal, {
            title: $t('deals.merge_deals'),
            size: 'md',
            dealId: props.dealId,
            onSubmit: async () => {
              uiStore.closeModal(modalId);
              await dealStore.fetchDeal(props.dealId);
              emit('deal-updated', props.deal);
            },
            onCancel: () => {
              uiStore.closeModal(modalId);
            },
          });
        });
      };

      // The drawer owns deletion: it knows the linked project and closes itself afterwards
      const handleDelete = () => emit('delete');

      onMounted(() => {
        fetchMembers();
      });

      return {
        editForm,
        ownerOptions,
        sourceLocked,
        hasChanges,
        saving,
        resetForm,
        saveChanges,
        handleMerge,
        handleDelete,
      };
    },
  };
</script>

<style scoped lang="scss">
  .deal-settings {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &__card {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      padding: 1rem;
      background: white;
      border: 1px solid var(--slate-300);
      border-radius: 4px;

      &--rows {
        gap: 0;
      }
    }

    &__card-title {
      margin: 0;
      color: var(--slate-900);
      font-size: 13px;
      font-weight: 500;
      line-height: 20px;
    }

    &__row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1rem;
      align-items: start;

      @include sm-down {
        grid-template-columns: 1fr;
      }
    }

    &__field {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      min-width: 0;

      label {
        color: var(--slate-900);
        font-size: 13px;
        font-weight: 500;
        line-height: 20px;
      }
    }

    &__field-hint {
      color: var(--slate-500);
      font-size: 11px;
      line-height: 20px;
    }

    // Shared control shell: 36px high, 4px radius, 13px text
    &__control {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      height: 36px;
      padding: 0 0.75rem;
      border: 1px solid var(--slate-300);
      border-radius: 4px;
      background: white;
      color: var(--slate-900);
      font-family: inherit;
      font-size: 13px;
      line-height: 20px;

      &:focus-within {
        border-color: var(--brand-500);
        outline: 2px solid var(--brand-100);
        outline-offset: -1px;
      }

      input,
      select {
        flex: 1;
        min-width: 0;
        height: 100%;
        padding: 0;
        border: 0;
        background: transparent;
        color: inherit;
        font-family: inherit;
        font-size: inherit;
        line-height: inherit;

        &:focus {
          outline: none;
        }
      }

      .icon {
        color: var(--slate-500);
      }

      &--select {
        position: relative;

        select {
          appearance: none;
          cursor: pointer;
        }

        .icon {
          pointer-events: none;
        }
      }

      &--locked {
        background: var(--slate-50);

        input:disabled {
          color: var(--slate-900);
          -webkit-text-fill-color: var(--slate-900);
          cursor: default;
        }
      }

      &--textarea {
        height: auto;
        min-height: 48px;
        padding: 0.5rem 0.75rem;
        resize: vertical;
      }
    }

    &__prefix {
      flex-shrink: 0;
      color: var(--slate-600);
    }

    &__divider {
      height: 1px;
      background: var(--slate-200);
    }

    &__actions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 0.5rem;
    }

    &__manage-row {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.75rem 0;

      @include sm-down {
        flex-direction: column;
        align-items: flex-start;
      }
    }

    &__manage-text {
      display: flex;
      flex: 1;
      flex-direction: column;
      gap: 0.25rem;
      min-width: 0;

      strong {
        color: var(--slate-900);
        font-size: 13px;
        font-weight: 500;
        line-height: 20px;
      }

      span {
        color: var(--slate-500);
        font-size: 11px;
        line-height: 20px;
      }
    }

    &__btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 0.375rem;
      height: 32px;
      padding: 0 0.75rem;
      border: 1px solid var(--slate-300);
      border-radius: 4px;
      background: white;
      color: var(--slate-700);
      font-family: inherit;
      font-size: 13px;
      font-weight: 500;
      line-height: 20px;
      white-space: nowrap;
      cursor: pointer;
      transition:
        background-color 0.15s ease,
        border-color 0.15s ease,
        color 0.15s ease;

      @include hover {
        &:not(:disabled) {
          background: var(--slate-50);
          border-color: var(--slate-400);
        }
      }

      &:focus-visible {
        outline: 2px solid var(--brand-300);
        outline-offset: 1px;
      }

      &--plain {
        height: 36px;
        border-color: transparent;
        color: var(--slate-600);

        &:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }

        @include hover {
          &:not(:disabled) {
            background: var(--slate-100);
            border-color: transparent;
          }
        }
      }

      &--danger {
        color: var(--red-600);

        @include hover {
          &:not(:disabled) {
            background: var(--red-50);
            border-color: var(--red-100);
          }
        }
      }
    }
  }
</style>
